import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { authedFileUrl } from "@/pages/job-cards/components/PhotoUploader";
import { SaveBar } from "./SettingsLayout";
import { GST_RATES, previewNumber } from "./lib";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "numbering", label: "Numbering" },
  { key: "gst",       label: "GST Defaults" },
  { key: "terms",     label: "Terms" },
  { key: "payment",   label: "Payment Details" },
  { key: "print",     label: "Print Options" },
];

const DOCS = [
  { key: "job_card",  label: "Job Card"  },
  { key: "estimate",  label: "Estimate"  },
  { key: "invoice",   label: "Invoice"   },
  { key: "gate_pass", label: "Gate Pass" },
];

export default function DocumentSettings() {
  const { user } = useAuth();
  const readOnly = user?.role !== "owner";
  const [tab, setTab] = useState("numbering");
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/document-settings");
      setInitial(r.data); setForm(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load settings"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = form && JSON.stringify(form) !== JSON.stringify(initial);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/settings/document-settings", form);
      setInitial(r.data); setForm(r.data);
      toast.success("Document settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("context", "payment_qr");
    try {
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, payment_details: { ...f.payment_details, qr_file_id: r.data.path } }));
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  if (loading || !form) return <div className="p-8 text-center text-[13px] text-[#667085]">Loading…</div>;

  const setNumbering = (k, patch) =>
    setForm((f) => ({ ...f, numbering: { ...f.numbering, [k]: { ...f.numbering[k], ...patch } } }));
  const setGst = (patch) => setForm((f) => ({ ...f, gst_defaults: { ...f.gst_defaults, ...patch } }));
  const setTerms = (patch) => setForm((f) => ({ ...f, terms: { ...f.terms, ...patch } }));
  const setPay = (patch) => setForm((f) => ({ ...f, payment_details: { ...f.payment_details, ...patch } }));
  const setPrint = (patch) => setForm((f) => ({ ...f, print_options: { ...f.print_options, ...patch } }));

  const year = new Date().getFullYear();

  return (
    <div className="space-y-4" data-testid="settings-documents">
      <nav className="flex gap-1 overflow-x-auto bg-white rounded-lg border border-[#E4E7EC] p-1"
           data-testid="doc-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  data-testid={`doc-tab-${t.key}`}
                  className={`h-9 px-3 rounded-md text-[11.5px] font-semibold whitespace-nowrap ${
                    tab === t.key ? "bg-[#1d3557] text-white" : "text-[#667085] hover:text-[#1d3557]"
                  }`}>
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "numbering" && (
        <section className="bg-white rounded-xl border border-[#E4E7EC]" data-testid="doc-numbering">
          <header className="px-4 py-3 border-b border-[#F1FAEE]">
            <h3 className="text-[13px] font-bold text-[#1d3557]">Numbering</h3>
            <p className="text-[11.5px] text-[#667085]">
              Configure prefix and starting sequence. Year is handled automatically.
            </p>
          </header>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {DOCS.map((d) => {
              const scheme = form.numbering[d.key];
              return (
                <div key={d.key} className="rounded-lg border border-[#E4E7EC] p-3">
                  <div className="text-[12px] font-bold text-[#1d3557] mb-2">{d.label}</div>
                  <div className="grid grid-cols-3 gap-2">
                    <F label="Prefix">
                      <I value={scheme.prefix} onChange={(e) => setNumbering(d.key, { prefix: e.target.value.toUpperCase() })}
                         readOnly={readOnly} data-testid={`num-${d.key}-prefix`}
                         className="font-mono uppercase" />
                    </F>
                    <F label="Start #">
                      <I type="number" min={1} value={scheme.starting_seq}
                         onChange={(e) => setNumbering(d.key, { starting_seq: Number(e.target.value) || 1 })}
                         readOnly={readOnly} data-testid={`num-${d.key}-start`}
                         className="font-mono" />
                    </F>
                    <F label="Length">
                      <I type="number" min={3} max={8} value={scheme.length}
                         onChange={(e) => setNumbering(d.key, { length: Number(e.target.value) || 6 })}
                         readOnly={readOnly} data-testid={`num-${d.key}-length`}
                         className="font-mono" />
                    </F>
                  </div>
                  <label className="inline-flex items-center gap-2 mt-3 text-[12px] text-[#1d3557]">
                    <input type="checkbox" checked={!!scheme.include_year}
                           onChange={(e) => setNumbering(d.key, { include_year: e.target.checked })}
                           disabled={readOnly} data-testid={`num-${d.key}-year`}
                           className="w-4 h-4 accent-[#e63946]" />
                    Include year in number
                  </label>
                  <div className="mt-3 rounded-md bg-[#F1FAEE] border border-[#a8dadc]/40 px-3 py-2 text-[12.5px] text-[#1d3557]">
                    Preview: <span className="font-mono font-bold" data-testid={`num-${d.key}-preview`}>
                      {previewNumber(scheme, year)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {tab === "gst" && (
        <Card title="GST defaults" testId="doc-gst">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <F label="Default invoice type">
              <select value={form.gst_defaults.default_invoice_type}
                      onChange={(e) => setGst({ default_invoice_type: e.target.value })}
                      disabled={readOnly} data-testid="gst-invoice-type"
                      className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
                <option value="gst">GST Invoice</option>
                <option value="non_gst">Non-GST</option>
              </select>
            </F>
            <F label="Default price type">
              <select value={form.gst_defaults.default_price_type}
                      onChange={(e) => setGst({ default_price_type: e.target.value })}
                      disabled={readOnly} data-testid="gst-price-type"
                      className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
                <option value="exclusive">Tax Exclusive</option>
                <option value="inclusive">Tax Inclusive</option>
              </select>
            </F>
            <F label="Default GST rate">
              <select value={form.gst_defaults.default_gst_rate}
                      onChange={(e) => setGst({ default_gst_rate: Number(e.target.value) })}
                      disabled={readOnly} data-testid="gst-default-rate"
                      className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white font-mono">
                {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
              </select>
            </F>
          </div>
        </Card>
      )}

      {tab === "terms" && (
        <Card title="Default terms" testId="doc-terms">
          <div className="grid grid-cols-1 gap-3">
            {["estimate", "job_card", "invoice"].map((k) => (
              <F key={k} label={`${k === "job_card" ? "Job Card" : k[0].toUpperCase() + k.slice(1)} terms`}>
                <textarea rows={3} value={form.terms[k] || ""}
                          onChange={(e) => setTerms({ [k]: e.target.value })}
                          readOnly={readOnly} data-testid={`terms-${k}`}
                          className="w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
              </F>
            ))}
            <p className="text-[11px] text-[#667085]">
              Gate Pass terms are fixed by Fender in Phase 1.
            </p>
          </div>
        </Card>
      )}

      {tab === "payment" && (
        <Card title="Payment details" testId="doc-payment">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="UPI ID">
              <I value={form.payment_details.upi_id}
                 onChange={(e) => setPay({ upi_id: e.target.value })}
                 readOnly={readOnly} data-testid="pay-upi"
                 placeholder="business@upi" className="font-mono" />
            </F>
            <F label="Bank name">
              <I value={form.payment_details.bank_name}
                 onChange={(e) => setPay({ bank_name: e.target.value })}
                 readOnly={readOnly} data-testid="pay-bank" />
            </F>
            <F label="Account name">
              <I value={form.payment_details.account_name}
                 onChange={(e) => setPay({ account_name: e.target.value })}
                 readOnly={readOnly} data-testid="pay-acct-name" />
            </F>
            <F label="Account number">
              <I value={form.payment_details.account_number}
                 onChange={(e) => setPay({ account_number: e.target.value })}
                 readOnly={readOnly} data-testid="pay-acct-number"
                 className="font-mono" />
            </F>
            <F label="IFSC">
              <I value={form.payment_details.ifsc}
                 onChange={(e) => setPay({ ifsc: e.target.value.toUpperCase() })}
                 readOnly={readOnly} data-testid="pay-ifsc"
                 className="font-mono" />
            </F>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] mb-1">
                Payment QR
              </div>
              <div className="flex items-center gap-3 rounded-lg border border-dashed border-[#E4E7EC] p-3">
                {form.payment_details.qr_file_id ? (
                  <img src={authedFileUrl(form.payment_details.qr_file_id)}
                       alt="Payment QR" className="h-16 rounded-md border border-[#E4E7EC] object-contain bg-white" />
                ) : (
                  <div className="h-16 w-16 grid place-items-center text-[#a8dadc] bg-[#F1FAEE] rounded-md">
                    <Upload size={16} />
                  </div>
                )}
                {!readOnly && (
                  <div className="flex gap-1.5">
                    <label className="h-8 px-2 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1 cursor-pointer"
                           data-testid="pay-qr-upload">
                      <Upload size={11} /> {form.payment_details.qr_file_id ? "Replace" : "Upload"}
                      <input type="file" accept="image/*" className="hidden"
                             onChange={(e) => upload(e.target.files?.[0])} />
                    </label>
                    {form.payment_details.qr_file_id && (
                      <button type="button" onClick={() => setPay({ qr_file_id: "" })}
                              data-testid="pay-qr-clear"
                              className="h-8 px-2 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#e63946] hover:bg-[#e63946]/8 inline-flex items-center gap-1">
                        <Trash2 size={11} /> Remove
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {tab === "print" && (
        <Card title="Print options" testId="doc-print">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              { k: "show_logo",         label: "Show garage logo" },
              { k: "show_gstin",        label: "Show GSTIN" },
              { k: "show_bank_details", label: "Show bank details" },
              { k: "show_payment_qr",   label: "Show payment QR" },
              { k: "show_signature",    label: "Show authorised signature" },
            ].map((o) => (
              <label key={o.k} className="inline-flex items-center gap-2 text-[13px] text-[#1d3557] py-1">
                <input type="checkbox" checked={!!form.print_options[o.k]}
                       onChange={(e) => setPrint({ [o.k]: e.target.checked })}
                       disabled={readOnly} data-testid={`print-${o.k}`}
                       className="w-4 h-4 accent-[#e63946]" />
                {o.label}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-[#667085] mt-3">
            Fender manages the document layout. These options control which sections appear.
          </p>
        </Card>
      )}

      {!readOnly && <SaveBar dirty={!!dirty} saving={saving} onSave={save} />}
      {readOnly && (
        <div className="rounded-md bg-[#F1FAEE] border border-[#a8dadc]/40 px-3 py-2 text-[12px] text-[#457b9d]">
          Only the Garage Owner can edit document settings.
        </div>
      )}
    </div>
  );
}

function Card({ title, children, testId }) {
  return (
    <section className="bg-white rounded-xl border border-[#E4E7EC]" data-testid={testId}>
      <header className="px-4 py-3 border-b border-[#F1FAEE]">
        <h3 className="text-[13px] font-bold text-[#1d3557]">{title}</h3>
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}
function F({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
function I({ className = "", ...props }) {
  return (
    <input {...props}
           className={`h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white focus:border-[#457b9d] outline-none ${className}`} />
  );
}
